import React, { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import CreateMealPlanClientScheduleSection from '@/components/meal-planning/CreateMealPlanClientScheduleSection';
import { Client } from '@/types/client';

export const MEAL_PLAN_MAX_DAYS = 7;
export const AI_MEAL_PLAN_MAX_DAYS = 6;
export const AI_MEAL_PLAN_MAX_MEAL_SLOTS = 5;
const DAY_IN_MS = 1000 * 60 * 60 * 24;

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDaysToDateOnly(value: string, days: number): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return formatDateInputValue(date);
}

function parseDateOnlyMillis(value: string): number | null {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function getInclusiveDateRangeDays(startDate: string, endDate: string): number | null {
  const startMs = parseDateOnlyMillis(startDate);
  const endMs = parseDateOnlyMillis(endDate);
  if (startMs == null || endMs == null) return null;
  return Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
}

type MealPlanScheduleFieldsProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  watch?: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  getValues?: UseFormGetValues<TFieldValues>;
  clientFieldName?: Path<TFieldValues>;
  startDateFieldName?: Path<TFieldValues>;
  endDateFieldName?: Path<TFieldValues>;
  clientLabel?: string;
  tipContent?: React.ReactNode;
  showTipIcon?: boolean;
  clientFooter?: React.ReactNode;
  maxPlanLengthDays?: number;
  todayDate?: string;
  onClientSelected?: (clientId: string, client: Client) => void;
};

export const MealPlanScheduleFields = <TFieldValues extends FieldValues>({
  control,
  errors,
  watch,
  setValue,
  getValues,
  clientFieldName = 'clientId' as Path<TFieldValues>,
  startDateFieldName = 'startDate' as Path<TFieldValues>,
  endDateFieldName = 'endDate' as Path<TFieldValues>,
  clientLabel = 'Client Name',
  tipContent,
  showTipIcon = true,
  clientFooter,
  maxPlanLengthDays = 7,
  todayDate,
  onClientSelected,
}: MealPlanScheduleFieldsProps<TFieldValues>) => {
  const today = useMemo(
    () => todayDate || new Date().toISOString().slice(0, 10),
    [todayDate],
  );

  const startDateValue = watch
    ? (watch(startDateFieldName) as string | undefined)
    : (useWatch({
        control,
        name: startDateFieldName,
      }) as string | undefined);
  const endDateValue = watch
    ? (watch(endDateFieldName) as string | undefined)
    : (useWatch({
        control,
        name: endDateFieldName,
      }) as string | undefined);

  const maxSelectableEndDate = useMemo(() => {
    if (!startDateValue) return undefined;
    return addDaysToDateOnly(startDateValue, maxPlanLengthDays - 1);
  }, [startDateValue, maxPlanLengthDays]);

  const getFieldError = (fieldName: Path<TFieldValues>): string | undefined => {
    const fieldError = (errors as any)?.[fieldName];
    return fieldError?.message;
  };

  return (
    <CreateMealPlanClientScheduleSection
      clientLabel={clientLabel}
      tipContent={tipContent}
      showTipIcon={showTipIcon}
      clientField={
        <div className="space-y-2">
          <Controller
            name={clientFieldName}
            control={control}
            rules={{ required: 'Please select a client' }}
            render={({ field }) => (
              <SearchableClientDropdown
                value={field.value}
                onChange={(clientId, client) => {
                  field.onChange(clientId);
                  if (onClientSelected) {
                    onClientSelected(clientId, client);
                  }
                }}
                placeholder="Search and select a client..."
                error={getFieldError(clientFieldName)}
              />
            )}
          />
          {clientFooter}
        </div>
      }
      startDateField={
        <Controller
          name={startDateFieldName}
          control={control}
          rules={{
            required: 'Please select a start date',
            validate: (value) => {
              const selectedDate = new Date(value);
              const todayDate = new Date(today);
              todayDate.setHours(0, 0, 0, 0);
              if (selectedDate < todayDate) {
                return 'Start date cannot be in the past';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor={`${String(startDateFieldName)}-input`}>Start Date</Label>
              <Input
                id={`${String(startDateFieldName)}-input`}
                type="date"
                min={today}
                value={field.value}
                onChange={(event) => {
                  const nextStartDate = event.target.value;
                  if (!nextStartDate) {
                    field.onChange(nextStartDate);
                    return;
                  }

                  field.onChange(nextStartDate);
                  if (startDateValue && endDateValue) {
                    const durationDays = getInclusiveDateRangeDays(startDateValue, endDateValue);
                    if (durationDays != null) {
                      setValue(endDateFieldName, addDaysToDateOnly(nextStartDate, durationDays - 1) as any, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                  }
                }}
              />
              {getFieldError(startDateFieldName) && (
                <p className="text-sm text-red-500">
                  {getFieldError(startDateFieldName)}
                </p>
              )}
            </div>
          )}
        />
      }
      endDateField={
        <Controller
          name={endDateFieldName}
          control={control}
          rules={{
            required: 'Please select an end date',
            validate: (value) => {
              const endMs = parseDateOnlyMillis(value);
              const startValueRaw = getValues
                ? (getValues(startDateFieldName) as string | undefined)
                : startDateValue;
              const todayMs = parseDateOnlyMillis(today);

              if (endMs == null || todayMs == null) {
                return 'Please select a valid end date';
              }
              if (endMs < todayMs) {
                return 'End date cannot be in the past';
              }
              if (!startValueRaw) {
                return 'Please select a start date first';
              }
              const startMs = parseDateOnlyMillis(startValueRaw);
              if (startMs == null) {
                return 'Please select a start date first';
              }
              if (endMs < startMs) {
                return 'End date cannot be before start date';
              }
              const durationDays = Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
              if (durationDays > maxPlanLengthDays) {
                return `Meal plans cannot exceed ${maxPlanLengthDays} days`;
              }

              return true;
            },
          }}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor={`${String(endDateFieldName)}-input`}>End Date</Label>
              <Input
                id={`${String(endDateFieldName)}-input`}
                type="date"
                min={startDateValue || today}
                max={maxSelectableEndDate}
                value={field.value}
                onChange={field.onChange}
              />
              {getFieldError(endDateFieldName) && (
                <p className="text-sm text-red-500">
                  {getFieldError(endDateFieldName)}
                </p>
              )}
            </div>
          )}
        />
      }
    />
  );
};

export default MealPlanScheduleFields;
